import React, { useRef, useState, useEffect } from 'react';
import { VStack, BoxProps, Text, Button } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styled from 'styled-components';
import { RefreshIcon } from '../../../../../components/icons';
import { Content } from './shared/types';
import { ContentComponent } from './shared/ContentComponent';
import { useIsMobile } from '../../../../../hooks/isMobile';

type Card = {
  content?: Content[];
  alternativeContent?: Content[];
};

interface CardFlipProps extends BoxProps {
  cards?: Card[];
  card?: Card;
  debug?: boolean;
}

const StyledVStack = styled(VStack)`
  transition: all 350ms ease-in-out;
  transform: rotateY(0deg);
  &.alternative {
    transform: rotateY(90deg);
  }
` as typeof VStack;

const ContentWrapper = styled.div<{ isVisible: boolean }>`
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 175ms ease-in-out;
  position: ${props => props.isVisible ? 'relative' : 'absolute'};
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
`;

export const mapAlign = (align: Content['align']) => {
  switch (align) {
    case 'left':
      return 'start';
    case 'center':
      return 'center';
    case 'right':
      return 'end';
    case 'justify':
      return 'stretch';
  }
  return align;
};

const CardFlip: React.FC<CardFlipProps> = ({
  cards,
  card,
  debug,
  ...props
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const alternativeContentRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const [state, setState] = useState([]);
  const [isAlternative, setIsAlternative] = useState(false);
  const [isAlternativeContent, setIsAlternativeContent] = useState(false);
  const [flipAnnouncement, setFlipAnnouncement] = useState('');
  const [minWidth, setMinWidth] = useState<number | undefined>(undefined);
  const isMobile = useIsMobile();
  const propsToPass = minWidth ? { ...props, minWidth } : props;

  const flip = () => {
    if (ref.current) {
      setMinWidth(ref.current.clientWidth);
    }
    setIsAlternative(true);
    setTimeout(() => {
      setIsAlternativeContent(!isAlternativeContent);
      setIsAlternative(false);
      setFlipAnnouncement('');
      requestAnimationFrame(() => setFlipAnnouncement(t('plugin.cardFlip.cardFlipped')));
    }, 350);
    setTimeout(() => {
      setMinWidth(undefined);
    }, 700);
  };

  const content = (
    <VStack w="full" spacing={0} gap={2}>
      {card?.content?.map((c, index) => (
        <React.Fragment key={`content-${index}`}>
          {ContentComponent({ width: 'full', ...c })}
        </React.Fragment>
      ))}
    </VStack>
  );

  const alternativeContent = (
    <VStack w="full" spacing={0} gap={2}>
      {card?.alternativeContent?.map((c, index) => (
        <React.Fragment key={`alt-content-${index}`}>
          {ContentComponent({ ...c, w: 'full' })}
        </React.Fragment>
      ))}
    </VStack>
  );

  const ret = (
    <StyledVStack
      ref={ref}
      borderRadius="8px"
      py={4}
      px={4}
      boxShadow="0 3px 15px rgba(0, 0, 0, 0.1)"
      className={classNames({ alternative: isAlternative })}
      spacing={0}
      justify="space-between"
      gap={4}
      {...propsToPass}
      height="100%"
      width="100%"
      maxWidth={isMobile ? '100%' : '450px'}
    >
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {flipAnnouncement}
      </div>
      <VStack
        w="full"
        spacing={0}
        gap={2}
        position="relative"
        transition="min-height 350ms ease-in-out"
        cursor="pointer"
        onClick={flip}
        tabIndex={0}
        role="button"
        aria-label={isAlternativeContent ? t('plugin.cardFlip.showFront') : t('plugin.cardFlip.showBack')}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            flip();
          }
        }}
        _focusVisible={{ outline: '3px solid var(--stemi-focus-color)', outlineOffset: '3px', borderRadius: '4px' }}
      >
        <ContentWrapper 
          ref={contentRef}
          isVisible={!isAlternativeContent}
        >
          {content}
        </ContentWrapper>
        <ContentWrapper 
          ref={alternativeContentRef}
          isVisible={isAlternativeContent}
        >
          {alternativeContent}
        </ContentWrapper>
      </VStack>
      <Button
        size="sm"
        borderRadius="16px"
        leftIcon={<RefreshIcon boxSize="16px" />}
        aria-label="Rotate"
        colorScheme="blue"
        minHeight="30px"
        onClick={(e) => {
          e.stopPropagation();
          flip();
        }}
      >
        {t('plugin.cardFlip.turn')}
      </Button>
    </StyledVStack>
  );

  if (debug) {
    return (
      <VStack>
        {ret}
        <Text>
          <pre>{JSON.stringify(state, null, 2)}</pre>
        </Text>
      </VStack>
    );
  }

  return <>{ret}</>;
};

export default CardFlip;
